import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

interface ActiveInteraction {
  id: number;
  status: string;
}

export interface PortalInteractionLockContextValue {
  activeStatus: string | null;
  beginInteraction: (status: string) => number;
  endInteraction: (interactionId: number) => void;
  isInteractionLocked: boolean;
}

const PortalInteractionLockContext =
  createContext<PortalInteractionLockContextValue | null>(null);

interface PortalInteractionLockProviderProps {
  children: ReactNode;
}

export const PortalInteractionLockProvider = ({
  children,
}: PortalInteractionLockProviderProps) => {
  const [activeInteractions, setActiveInteractions] = useState<
    ActiveInteraction[]
  >([]);
  const nextInteractionId = useRef(0);

  const beginInteraction = useCallback((status: string) => {
    nextInteractionId.current += 1;
    const interactionId = nextInteractionId.current;
    setActiveInteractions((currentInteractions) => [
      ...currentInteractions,
      { id: interactionId, status },
    ]);
    return interactionId;
  }, []);

  const endInteraction = useCallback((interactionId: number) => {
    setActiveInteractions((currentInteractions) =>
      currentInteractions.filter(
        (interaction) => interaction.id !== interactionId
      )
    );
  }, []);

  const contextValue = useMemo<PortalInteractionLockContextValue>(() => {
    const activeInteraction = activeInteractions[activeInteractions.length - 1];

    return {
      activeStatus: activeInteraction?.status ?? null,
      beginInteraction,
      endInteraction,
      isInteractionLocked: activeInteractions.length > 0,
    };
  }, [activeInteractions, beginInteraction, endInteraction]);

  return (
    <PortalInteractionLockContext.Provider value={contextValue}>
      {children}
    </PortalInteractionLockContext.Provider>
  );
};

export const usePortalInteractionLock = () => {
  const context = useContext(PortalInteractionLockContext);
  if (!context) {
    throw new Error(
      'usePortalInteractionLock must be used within PortalInteractionLockProvider'
    );
  }
  return context;
};

export const useOptionalPortalInteractionLock = () =>
  useContext(PortalInteractionLockContext);
